import { timeUtils } from '@sato/components';
import { endOfWeek, startOfWeek } from 'date-fns';

import { GA_EVENT_TYPES } from '@models/analyticsEvents.types';
import { SerializedPlainDate } from '@models/api/data.types';
import {
  SaunaReservationChangeRequest,
  SaunaReservationRequest,
  SaunaResource,
  SaunaShift,
  ScheduleShift,
} from '@models/sauna';
import { createAsyncTask } from '@stores/taskStore';
import { sendGoogleAnalyticsEvent } from '@utils/analytics.utils';
import { formatDateAndTime } from '@utils/date.utils';

import { authClient } from './client.api';

const SAUNA_RESERVATION_BASE_URL = `${process.env.RESERVATIONS_API_URL}/sauna`;

export const SAUNA_API_URLS = {
  SAUNAS: `${SAUNA_RESERVATION_BASE_URL}/resources`,
  SAUNA_SHIFTS: `${SAUNA_RESERVATION_BASE_URL}/availability`,
  CANCEL_SAUNA_RESERVATION: `${SAUNA_RESERVATION_BASE_URL}/cancel`,
  CHANGE_SAUNA_RESERVATION: `${SAUNA_RESERVATION_BASE_URL}/change`,
  SAUNA_RESERVATION: `${SAUNA_RESERVATION_BASE_URL}/reserve`,
  SAUNA_RESERVATIONS: `${SAUNA_RESERVATION_BASE_URL}/reservations`,
} as const;

const saunaCRUDApi = {
  getSaunas: (apartmentId: string) =>
    authClient.get<SaunaResource[]>(SAUNA_API_URLS.SAUNAS, {
      params: { apartmentId },
    }),

  getSaunaShifts: (payload: {
    resourceId: string;
    startDate: SerializedPlainDate;
    endDate: SerializedPlainDate;
  }) =>
    authClient.get<ScheduleShift[]>(SAUNA_API_URLS.SAUNA_SHIFTS, {
      params: payload,
    }),

  createSaunaReservation: createAsyncTask(
    'create-sauna-reservation',
    (payload: SaunaReservationRequest) =>
      authClient.post<SaunaShift>(SAUNA_API_URLS.SAUNA_RESERVATION, payload)
  ),

  getSaunaReservations: (payload: {
    apartmentId: string;
    startDate: string;
    endDate: string;
  }) =>
    authClient.get<SaunaShift[] | { status: number }>(
      SAUNA_API_URLS.SAUNA_RESERVATIONS,
      { params: payload }
    ),

  updateSaunasReservation: createAsyncTask(
    'update-sauna-reservation',
    (payload: SaunaReservationChangeRequest) =>
      authClient.post<SaunaShift>(
        SAUNA_API_URLS.CHANGE_SAUNA_RESERVATION,
        payload
      )
  ),

  removeSaunaReservation: (payload: {
    reservationId: string;
    apartmentId: string;
  }) =>
    authClient.get<unknown>(SAUNA_API_URLS.CANCEL_SAUNA_RESERVATION, {
      params: payload,
    }),
};

export const saunaApi = {
  getSaunas: saunaCRUDApi.getSaunas,

  getSaunaReservations: saunaCRUDApi.getSaunaReservations,

  reserve: async ({
    apartmentId,
    startDate,
    endDate,
    shiftLength,
    resourceId,
  }: {
    apartmentId: string;
    startDate: string;
    endDate: string;
    shiftLength: number;
    resourceId: string;
  }) => {
    const { date: isoStartDate, time: startTime } =
      formatDateAndTime(startDate);
    const { time: endTime } = formatDateAndTime(endDate);

    if (!isoStartDate || !startTime || !endTime) {
      throw Error('invalid shift data');
    }

    const response = await saunaCRUDApi.createSaunaReservation({
      resourceId,
      apartmentId,
      startDate: isoStartDate,
      startTime,
      endTime,
      shiftLength,
      reservationType: 'CONTINUOUS',
    });

    if (response.status === 200) return response.data;

    return undefined;
  },

  changeReservation: async ({
    reservationId,
    shift,
  }: {
    reservationId: string;
    shift: ScheduleShift;
  }) => {
    const { date: isoStartDate, time: startTime } = formatDateAndTime(
      shift.startDateTime
    );
    const { time: endTime, date: isoEndDate } = formatDateAndTime(
      shift.endDateTime
    );

    const response = await saunaCRUDApi.updateSaunasReservation({
      startDate: isoStartDate,
      endDate: isoEndDate,
      startTime,
      endTime,
      reservationId,
    });

    return response.data;
  },

  cancelReservation: async (reservationId: string, apartmentId: string) => {
    const response = await saunaCRUDApi.removeSaunaReservation({
      reservationId,
      apartmentId,
    });

    if (response.status === 200) {
      sendGoogleAnalyticsEvent({
        event: GA_EVENT_TYPES.CANCEL_SAUNA_RESERVATION,
      });
    }

    return response.data;
  },

  getShifts: async (resourceId: string, dateOfTheSelectedWeek: Date) => {
    const startDate = timeUtils.formatISO(
      startOfWeek(dateOfTheSelectedWeek, { weekStartsOn: 1 }),
      { representation: 'date' }
    );
    const endDate = timeUtils.formatISO(
      endOfWeek(dateOfTheSelectedWeek, { weekStartsOn: 1 }),
      { representation: 'date' }
    );

    const response = await saunaCRUDApi.getSaunaShifts({
      resourceId,
      startDate,
      endDate,
    });

    if (response.status === 200) return response.data;

    return [];
  },
};
